import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { testResultService } from '@/services/testResultService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { Eye, EyeOff, Lock, AlertCircle, Save, Pencil, BarChart2, BookOpen } from 'lucide-react';

interface TestResult {
  id: string;
  testId: string;
  testTitle: string;
  score: number;
  maxScore: number;
  percentage: number;
  totalQuestions: number;
  correctAnswers: number;
  completedAt: string;
  attemptNumber?: number;
  totalAttempts?: number;
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [results, setResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Change password state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordError, setPasswordError] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        // Use testResultService to get all student results with multiple attempts support
        const allResults = await testResultService.getStudentResults();

        // Filter to show completed-like tests (exclude IN_PROGRESS)
        const completedResults = allResults.filter(result =>
          result.status === 'COMPLETED' || result.status === 'SUBMITTED' || result.status === 'expired'
        );

        // Sort by most recent first and ensure each attempt is shown separately
        const sortedResults = completedResults.sort((a, b) =>
          new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
        );

        setResults(sortedResults);
      } catch (error) {
        console.error('Error fetching results:', error);
        toast({
          title: 'Error',
          description: 'Failed to load your test results. Please try again later.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [toast]);

  const handleSaveProfile = async () => {
    if (!user) return;

    setIsSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        '/api/users/profile',
        { name, email },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully!',
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update your profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      return 'Password must contain at least one special character (@$!%*?&)';
    }
    return null;
  };

  const handlePasswordChange = async () => {
    setPasswordError('');

    // Validate new password
    const passwordValidationError = validatePassword(passwordData.newPassword);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    // Check password confirmation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        '/api/users/change-password',
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast({
        title: 'Password Updated',
        description: 'Your password has been changed successfully!',
      });

      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsChangingPassword(false);
    } catch (error: any) {
      console.error('Error changing password:', error);
      setPasswordError(
        error.response?.data?.message || 'Failed to change password. Please try again.'
      );
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handlePasswordInputChange = (field: keyof typeof passwordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    setPasswordError(''); // Clear error when user starts typing
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold mb-4">User Not Found</h2>
        <p className="text-muted-foreground mb-6">
          Please log in to view your profile.
        </p>
        <Link to="/login">
          <Button>Login</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl bg-gray-50 min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <Card className="bg-card border border-border">
            <CardHeader className="bg-primary/10 rounded-t-xl">
              <CardTitle className="text-foreground font-bold font-heading">Profile</CardTitle>
              <CardDescription className="text-muted-foreground font-medium font-body">Manage your account details</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-1 font-body">
                      Name
                    </label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="border-border focus:ring-primary focus:border-primary bg-card"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1 font-body">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-border focus:ring-primary focus:border-primary bg-card"
                    />
                  </div>
                  <div className="flex space-x-2 pt-2">
                    <Button
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl transition-all duration-200"
                    >
                      {isSaving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" aria-hidden="true" />Save</>}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        setName(user.name || '');
                        setEmail(user.email);
                      }}
                      className="border-border text-foreground hover:bg-muted font-semibold rounded-xl"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground font-body">Username</p>
                    <p className="text-foreground font-medium font-body">{user.username}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground font-body">Name</p>
                    <p className="text-foreground font-medium font-body">{user.name || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground font-body">Email</p>
                    <p className="text-foreground font-medium font-body">{user.email}</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    className="mt-2 border-border text-foreground hover:bg-muted font-semibold rounded-xl"
                  >
                    <Pencil className="w-4 h-4 mr-2" aria-hidden="true" />Edit Profile
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card className="bg-card border border-border">
            <CardHeader className="bg-primary/10 rounded-t-xl">
              <CardTitle className="flex items-center gap-2 text-foreground font-bold font-heading">
                <Lock className="h-5 w-5 text-primary" />
                Change Password
              </CardTitle>
              <CardDescription className="text-muted-foreground font-medium font-body">Update your account password</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {!isChangingPassword ? (
                <Button
                  variant="outline"
                  onClick={() => setIsChangingPassword(true)}
                  className="w-full border-border text-foreground hover:bg-muted font-semibold rounded-xl"
                >
                  <Lock className="w-4 h-4 mr-2" aria-hidden="true" />Change Password
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        placeholder="Enter current password"
                        value={passwordData.currentPassword}
                        onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        placeholder="Enter new password"
                        value={passwordData.newPassword}
                        onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>Password must contain:</p>
                      <ul className="list-disc list-inside space-y-0.5 ml-2">
                        <li>At least 8 characters</li>
                        <li>One uppercase letter</li>
                        <li>One lowercase letter</li>
                        <li>One number</li>
                        <li>One special character (@$!%*?&)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {passwordError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{passwordError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex space-x-2 pt-2">
                    <Button
                      onClick={handlePasswordChange}
                      disabled={isUpdatingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    >
                      {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                        setPasswordError('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="bg-card border border-border">
            <CardHeader className="bg-primary/10 rounded-t-xl">
              <CardTitle className="text-foreground font-bold font-heading">Test History</CardTitle>
              <CardDescription className="text-muted-foreground font-medium font-body">Your recent test results</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-primary mx-auto"></div>
                  <p className="mt-4 text-muted-foreground font-medium font-body">Loading your results...</p>
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BarChart2 className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <p className="mb-4 text-muted-foreground font-medium font-body">You haven't taken any tests yet.</p>
                  <Link to="/tests">
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl transition-all duration-200">
                      <BookOpen className="w-4 h-4 mr-2" aria-hidden="true" />Browse Tests
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {results.map((result) => {
                    const scorePercentage = result.percentage || ((result.score / result.maxScore) * 100);
                    const formattedDate = new Date(result.completedAt).toLocaleDateString();

                    return (
                      <div
                        key={`${result.testId}-${result.id}`}
                        className="border border-border rounded-xl p-4 hover:bg-muted transition-colors bg-card"
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                          <div>
                            <h3 className="font-bold text-foreground font-heading">
                              {result.testTitle || 'Test (Title Not Available)'}
                              {result.attemptNumber && (
                                <span className="ml-2 text-sm font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                  Attempt #{result.attemptNumber}
                                </span>
                              )}
                            </h3>
                            <p className="text-sm text-muted-foreground font-medium font-body">
                              Completed on {formattedDate}
                            </p>
                          </div>
                          <div className="mt-2 md:mt-0 flex items-center space-x-4">
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground font-semibold font-body">Score</p>
                              <p className="font-bold text-foreground font-heading">{scorePercentage.toFixed(1)}%</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground font-semibold font-body">Correct</p>
                              <p className="font-bold text-foreground font-heading">{result.correctAnswers}/{result.totalQuestions}</p>
                            </div>
                            <Link to={`/results/${result.id}`}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-border text-foreground hover:bg-muted font-semibold rounded-xl"
                              >
                                <Eye className="w-4 h-4 mr-2" aria-hidden="true" />View Details
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
